import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/feature/PageSEO";
import { categories } from "@/mocks/categories";

const SITE_URL = (import.meta as Record<string, unknown>).env && typeof (import.meta as Record<string, unknown>).env === "object"
  ? ((import.meta as Record<string, { VITE_SITE_URL?: string }>).env?.VITE_SITE_URL || "https://acbuyspreadsheet.courses")
  : "https://acbuyspreadsheet.courses";

const seo = {
  jsonld: [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "ACbuyspreadsheet Product Categories: Complete Shopping Directory",
      "description": "Navigate all 14 acbuyspreadsheet product categories with this comprehensive directory. From Shoes to Accessories, find direct links to every category on the main store.",
      "datePublished": "2026-06-24",
      "dateModified": "2026-06-24",
      "author": { "@type": "Organization", "name": "ACbuyspreadsheet" },
      "publisher": { "@type": "Organization", "name": "ACbuyspreadsheet", "url": SITE_URL },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}/article/acbuyspreadsheet-categories-guide` }
    }
  ],
  title: "ACbuyspreadsheet Product Categories Directory (2026) | All 14 Categories",
  description: "Navigate all 14 acbuyspreadsheet product categories with this comprehensive directory. From Shoes to Accessories, find direct links to every category on the main kaspreadsheet store.",
  canonicalPath: "/article/acbuyspreadsheet-categories-guide"
};

export default function CategoriesGuidePage() {
  return (
    <>
      <PageSEO {...seo} />
      <Navbar />
      <main>
        <section className="pt-28 pb-10 md:pt-36 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-foreground-500 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">Guide</span>
              <span>Updated June 2026</span>
              <span>9 min read</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold font-heading text-foreground-950 leading-tight mb-4">
              <strong>ACbuyspreadsheet</strong> Product Categories: Complete Shopping Directory
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              Navigate all 14 <strong>acbuyspreadsheet</strong> product categories with this comprehensive directory. From Shoes to Accessories, find direct links to every category on the main kaspreadsheet store.
            </p>
          </div>
        </section>

        <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">

            <section className="mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-5">Your Complete <strong>ACbuyspreadsheet</strong> Category Directory</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                One of the core strengths of the <strong>acbuyspreadsheet</strong> system is its comprehensive coverage of product categories. Whether you are looking for everyday essentials like T-Shirts and Underwear, or specialty items like Perfumes and Accessories, every category is organized and linked directly to the main store. Below is the complete directory with descriptions of what you will find in each section.
              </p>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-5">All 14 <strong>ACbuyspreadsheet</strong> Categories</h2>
              <div className="space-y-4">
                {categories.map((cat, idx) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer group">
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors">{idx + 1}. {cat.name}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">Click to browse on main store</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all flex-shrink-0"></i>
                  </a>
                ))}
              </div>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-5">How Categories Are Organized</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                The <strong>acbuyspreadsheet</strong> category structure follows a logical progression from apparel to accessories. Clothing items (Shoes, Hoodies, T-Shirts, Jackets, Pants, Sets, Jersey) come first, followed by accessories (Headwear, Bags, Belts, Perfumes), with Underwear and Other Stuff rounding out the collection. This intuitive organization means you never waste time figuring out where to look.
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                Each category link opens a pre-filtered results page on the main kaspreadsheet store, showing you exactly the products in that category — no additional searching or filtering needed. Return to the{" "}
                <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium underline">acbuyspreadsheet homepage</Link>{" "}
                to browse the full category grid with images.
              </p>
            </section>

            <section className="text-center py-8 md:py-10 px-6 rounded-lg bg-primary-50 border border-primary-200/60">
              <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">Ready to Explore All Categories?</h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">Browse the complete <strong>acbuyspreadsheet</strong> category collection now.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"><i className="ri-home-line"></i>Back to Home</Link>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold border-2 border-primary-300 text-primary-700 hover:bg-primary-100 transition-colors whitespace-nowrap"><i className="ri-external-link-line"></i>Visit Main Store</a>
              </div>
            </section>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}